import { Flex, Table, Text } from '@chakra-ui/react';
import { PredictionHistoryEntry } from '@features/positions/predictions/types';
import { memo } from 'react';
import { LuChevronRight } from 'react-icons/lu';

interface HistoryRowProps {
  entry: PredictionHistoryEntry;
  index: number;
  totalCount: number;
  onClick: (entry: PredictionHistoryEntry) => void;
}

const formatTime = (timeStr: string) => {
  const date = new Date(timeStr);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const HistoryRow = memo(({ entry, index, totalCount, onClick }: HistoryRowProps) => {
  const isPositive = entry.returnValue >= 0;

  return (
    <Table.Row fontSize="sm" cursor="pointer" _hover={{ bg: 'bg.subtle' }} onClick={() => onClick(entry)}>
      <Table.Cell
        border="none"
        p={4}
        borderTopLeftRadius={index === 0 ? 'xl' : 'none'}
        borderBottomLeftRadius={index === totalCount - 1 ? 'xl' : 'none'}
      >
        <Flex direction="column" gap={0.5}>
          <Text fontWeight="medium">{entry.market}</Text>
          <Text fontSize="sm" fontWeight="medium">You thought: {entry.outcome}</Text>
        </Flex>
      </Table.Cell>
      <Table.Cell border="none" p={4}>
        <Flex direction="column" gap={0.5}>
          <Text fontWeight="medium" textTransform="capitalize">{entry.activity}</Text>
          <Text fontSize="xs" color="fg.subtle">{formatTime(entry.time)}</Text>
        </Flex>
      </Table.Cell>
      <Table.Cell border="none" p={4}>
        <Text fontWeight="medium" color={isPositive ? 'green.600' : 'red.600'}>
          {isPositive ? '+' : '-'}${Math.abs(entry.returnValue).toFixed(2)}
        </Text>
      </Table.Cell>
      <Table.Cell
        border="none"
        p={4}
        pr={2}
        borderTopRightRadius={index === 0 ? 'xl' : 'none'}
        borderBottomRightRadius={index === totalCount - 1 ? 'xl' : 'none'}
        w="1"
      >
        <LuChevronRight size={16} color="var(--chakra-colors-text-secondary)" />
      </Table.Cell>
    </Table.Row>
  );
});
HistoryRow.displayName = 'HistoryRow';

interface Props {
  entries: PredictionHistoryEntry[];
  onRowClick: (entry: PredictionHistoryEntry) => void;
}

const HistoryTable = memo(({ entries, onRowClick }: Props) => {
  return (
    <Flex direction="column" bg="surface.muted" borderRadius="xl" px={2} pb={2} overflowX="auto">
      <Table.Root>
        <Table.Header fontSize="sm" fontWeight="medium">
          <Table.Row bg="transparent">
            <Table.ColumnHeader border="none" fontSize="sm" fontWeight="medium" color="text.secondary" p={4}>
              <Text>MARKET</Text>
            </Table.ColumnHeader>
            <Table.ColumnHeader border="none" fontSize="sm" fontWeight="medium" color="text.secondary" p={4}>
              <Text>ACTIVITY</Text>
            </Table.ColumnHeader>
            <Table.ColumnHeader border="none" fontSize="sm" fontWeight="medium" color="text.secondary" p={4}>
              <Text>RETURN</Text>
            </Table.ColumnHeader>
            <Table.ColumnHeader border="none" p={4} w="1" />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {entries.map((entry, index) => (
            <HistoryRow
              key={entry.id}
              entry={entry}
              index={index}
              totalCount={entries.length}
              onClick={onRowClick}
            />
          ))}
        </Table.Body>
      </Table.Root>
    </Flex>
  );
});
HistoryTable.displayName = 'HistoryTable';

export default HistoryTable;
